// src/pages/QuotesList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Quotes } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { downloadQuotePDF } from '../services/quotePdfGenerator';

const getApprovalStatus = (q) => {
  if (q.fin_id != null && q.fin_id !== "0") return 'completed';
  if (q.ac_id != null && q.ac_id !== "0")  return 'pending_finance';
  return 'pending_manager';
};

const STATUS_LABEL = {
  pending_manager: ['Pending Manager', '#92400e', '#fef3c7'],
  pending_finance: ['Pending Finance', '#1d4ed8', '#dbeafe'],
  completed:       ['Invoiced',        '#166534', '#dcfce7'],
};

export default function QuotesList() {
    const { user }  = useAuth();
    const navigate  = useNavigate();
    const [list,    setList]    = useState([]);
    const [busy,    setBusy]    = useState(true);
    const [filter,  setFilter]  = useState('all');
    const [search,  setSearch]  = useState('');
    const [pdfBusy, setPdfBusy] = useState(null);
    const [err,     setErr]     = useState('');

    const [stages, setStages]         = useState([]);
    const [stageModalFor, setStageModalFor] = useState(null);
    const [stagePick, setStagePick]   = useState('');
    const [stageReason, setStageReason] = useState('');
    const [stageSaving, setStageSaving] = useState(false);

    const isFinance = user.ryts == 3;

    const load = useCallback(() => {
        setBusy(true);
        Promise.all([Quotes.list(), Quotes.statuses()])
            .then(([quotes, stat]) => { setList(quotes); setStages(stat); })
            .catch(console.error)
            .finally(() => setBusy(false));
    }, []);
    useEffect(load, [load]);

    const stageById = (id) => stages.find(s => String(s.id) === String(id));

    const handleApprove = async (stage, id) => {
        setErr('');
        try {
            if (stage === 'manager') await Quotes.approveManager(id, user.id);
            else await Quotes.approveFinance(id, user.id);
            load();
        } catch (ex) { setErr(ex.message); }
    };

    const openStageModal = (q) => {
        setStageModalFor(q);
        setStagePick(String(q.qstatus || ''));
        setStageReason('');
        setErr('');
    };

    const saveStage = async () => {
        if (!stagePick) { setErr('Select a stage'); return; }
        setStageSaving(true); setErr('');
        try {
            await Quotes.updateStatus({
                id: stageModalFor.id,
                qstatus: stagePick,
                user_id: user.id,
                corp_id: user.corp_id,
                lostReason: stageReason || undefined,
            });
            setStageModalFor(null);
            load();
        } catch (ex) { setErr(ex.message); }
        finally { setStageSaving(false); }
    };

    const roleScoped = list.filter(q => {
        if (!isFinance) return true;
        const status = getApprovalStatus(q);
        return status === 'pending_finance' || status === 'completed';
    });

    const filtered = roleScoped.filter(q => {
        const matchSearch = !search ||
            String(q.id || '').toLowerCase().includes(search.toLowerCase()) ||
            (q.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (q.pono || '').toLowerCase().includes(search.toLowerCase());
        if (filter === 'quotes')   return matchSearch && q.invoiced == 0;
        if (filter === 'invoices') return matchSearch && q.invoiced >= 1;
        return matchSearch;
    });

    const downloadPDF = async (quote) => {
        setErr('');
        setPdfBusy(quote.id);
        try {
            await downloadQuotePDF({ quote_id: quote.id, corp_id: user.corp_id, user_id: user.id });
        } catch (ex) {
            setErr(`PDF error: ${ex.message}`);
        } finally {
            setPdfBusy(null);
        }
    };

    return (
        <div className="page">
            <div className="page-header">
                <div className="page-title">📋 {isFinance ? 'Invoices Awaiting Payment' : 'Quotes'}</div>
                <input className="search-bar" placeholder="Search name, PO…" value={search} onChange={e => setSearch(e.target.value)} />
                {!isFinance && <button className="btn btn-primary" onClick={() => navigate('/quotes/new')}>+ New</button>}
            </div>

            {err && (
                <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                    {err}
                    <button onClick={() => setErr('')} style={{ marginLeft: 8, cursor: 'pointer', background: 'none', border: 'none', fontWeight: 700 }}>✕</button>
                </div>
            )}

            {!isFinance && (
                <div className="tabs">
                    {[['all','All'], ['quotes','Quotes Only'], ['invoices','Invoices']].map(([k, l]) => (
                        <button key={k} className={`tab${filter === k ? ' active' : ''}`} onClick={() => setFilter(k)}>{l}</button>
                    ))}
                </div>
            )}

            {busy ? (
                <div className="spinner-wrap"><div className="spinner" /></div>
            ) : filtered.length === 0 ? (
                <div className="empty">{isFinance ? 'Nothing awaiting finance right now' : 'No records found'}</div>
            ) : (
                <div className="tbl-wrap">
                    <table className="tbl">
                        <thead>
                            <tr><th>#</th><th>Name</th><th>Customer</th><th>PO No</th><th>Date</th><th>Stage</th><th>Approval</th><th>Actions</th></tr>
                        </thead>
                        <tbody>
                            {filtered.map(q => {
                                const approvalStatus = getApprovalStatus(q);
                                const [label, color, bg] = STATUS_LABEL[approvalStatus];
                                const isDownloading = pdfBusy === q.id;
                                const stage = stageById(q.qstatus);
                                return (
                                    <tr key={q.id}>
                                        <td style={{ color: 'var(--muted)', fontSize: '.85rem' }}>{q.id}</td>
                                        <td><span className="text-accent fw-bold" style={{ cursor: 'pointer' }} onClick={() => navigate(`/quotes/${q.id}`)}>{q.name || '—'}</span></td>
                                        <td>{q.customer}</td>
                                        <td><code style={{ fontSize: '.8rem' }}>{q.pono || '—'}</code></td>
                                        <td style={{ fontSize: '.85rem' }}>{q.date_entered?.slice(0, 10)}</td>
                                        <td>
                                            <span className="badge" style={{
                                                background: stage?.bg || '#f1f5f9', color: stage?.color || '#64748b',
                                                border: `1px solid ${stage?.color || '#64748b'}40`, padding: '2px 8px',
                                                borderRadius: 12, fontSize: '.75rem', fontWeight: 600, cursor: 'pointer',
                                            }} onClick={() => openStageModal(q)} title="Click to change stage">
                                                {stage?.label || 'Set stage'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="badge" style={{ background: bg, color: color, border: `1px solid ${color}40`, padding: '2px 8px', borderRadius: 12, fontSize: '.75rem', fontWeight: 600 }}>
                                                {label}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap' }}>
                                                <button className="btn btn-sm btn-ghost" onClick={() => navigate(`/quotes/${q.id}`)}>
                                                    {!isFinance && approvalStatus === 'pending_manager' ? 'Edit' : 'View'}
                                                </button>
                                                <button className="btn btn-sm" style={{
                                                    background: isDownloading ? '#e2e8f0' : '#1a3a6e', color: isDownloading ? '#64748b' : '#fff',
                                                    border: 'none', cursor: isDownloading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                                                }} onClick={() => !isDownloading && downloadPDF(q)} title="Download PDF" disabled={isDownloading}>
                                                    {isDownloading ? 'PDF…' : '📄 PDF'}
                                                </button>
                                                {approvalStatus === 'pending_manager' && user.ryts == 1 && (
                                                    <button className="btn btn-sm btn-success" onClick={() => handleApprove('manager', q.id)}>✓ Approve</button>
                                                )}
                                                {approvalStatus === 'pending_finance' && isFinance && (
                                                    <button className="btn btn-sm btn-success" onClick={() => handleApprove('finance', q.id)}>✓ Mark Invoiced</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {stageModalFor && (
                <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setStageModalFor(null)}>
                    <div className="modal">
                        <div className="modal-header">
                            <span className="modal-title">Update Stage — {stageModalFor.name || `#${stageModalFor.id}`}</span>
                            <button className="btn-icon" onClick={() => setStageModalFor(null)}>✕</button>
                        </div>
                        {err && <div className="alert alert-error">{err}</div>}
                        <label style={{ marginBottom: '1rem' }}>
                            Stage
                            <select value={stagePick} onChange={e => setStagePick(e.target.value)}>
                                <option value="">-- Select --</option>
                                {stages.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                            </select>
                        </label>
                        {stageById(stagePick)?.category === 'lost' && (
                            <label style={{ marginBottom: '1.2rem' }}>
                                Notes <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional, appears in customer history)</span>
                                <textarea rows={2} value={stageReason} onChange={e => setStageReason(e.target.value)} placeholder="e.g. Client sourced locally, price too high…" />
                            </label>
                        )}
                        <div className="modal-footer">
                            <button className="btn btn-primary" onClick={saveStage} disabled={stageSaving}>{stageSaving ? 'Saving…' : 'Update Stage'}</button>
                            <button className="btn btn-ghost" onClick={() => setStageModalFor(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
